import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;  
  font-family: 'sans-serif';
`;

const FormTitle = styled.h1`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  text-decoration: underline;
  margin: 30px 0;
  color: #2c3e50;
`;

const FormContent = styled.div`
  font-family: sans-serif;
  line-height: 1.6;
  color: #333;
`;

const PolicyText = styled.div`
  margin: 20px 0;
  font-size: 1rem;
  line-height: 1.8;
  text-align: left;
`;

const PolicyParagraph = styled.p`
  margin: 15px 0;
  text-indent: 0;
`;

const SignatureSection = styled.div`
  margin: 40px 0;
`;

const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0 0;
  font-size: 1rem;
`;

const SigLabel = styled.span`
  color: #222;
  min-width: 150px;
  display: inline-block;
`;

const SigValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 300px;
  display: inline-block;
`;

const SignatureImage = styled.img`
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
  border: none;
  background: transparent;
  border-bottom: 1px solid #333;
  margin-bottom: 18px;
`;

const DateValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 150px;
  display: inline-block;
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 600px;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const CellPhonePolicyForm = () => {
  const { email } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  return (
    <Container>
      <PrintHideStyle />
      <div ref={docRef} style={{ background: '#fff', color: '#222', padding: 0 }}>
        
        <Logo src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" />
        
        <FormTitle>CELL PHONE POLICY</FormTitle>
        
        <FormContent>
          <PolicyText>
            <PolicyParagraph>
              Students may have "silenced" cell phones and mobile communication devices on their person.
            </PolicyParagraph>
            
            <PolicyParagraph>
              The use of these devices during instructional time without the permission of the instructor, or in a disruptive manner in the school setting is prohibited. This includes texting, emailing, checking texts and emails and other actions that distract attention from instructional activities.
            </PolicyParagraph>
            
            <PolicyParagraph>
              Cell phones and mobile communication devices that ring, vibrate excessively, or in any other way disrupt the educational process of a class, are grounds for the student to be asked to leave the classroom by the instructor.
            </PolicyParagraph>
            
            <PolicyParagraph>
              Students are only permitted to use cell phones and mobile communication devices during their break and lunch period. Use of such devices in classrooms will be restricted and violations will result in a conference with your instructor and or case worker.
            </PolicyParagraph>
            
            <PolicyParagraph>
              No student shall use a cell phone or mobile communication device to violate any Student Code of Conduct.
            </PolicyParagraph>
            
            <PolicyParagraph>
              Examples of unacceptable usage can include but are not limited to: in restrooms or hallways during instructional time, during lab or clinical, cheating, cyber bullying, sexting, taking pictures or videos, using phones in places of school business, and etc.
            </PolicyParagraph>
            
            <PolicyParagraph>
              Each teacher has the right to permit the use of cell phones and mobile communication devices for instructional purposes. Any student found not adhering to this policy can face disciplinary action causing possible dismissal from the program.
            </PolicyParagraph>
          </PolicyText>

          <SignatureSection>
            <SignatureRow>
              <SigLabel>Student Name (Print):</SigLabel>
              <SigValue>{student.firstName} {student.lastName}</SigValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Student Signature:</SigLabel>
              {(() => {
                const sigData = student.student_ack_signature_data;
                
                // Check if it's a valid data URL (must have content after data:)
                if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
                  return (
                    <SignatureImage src={sigData} alt="Student Signature" />
                  );
                }
                
                // Check if it's SVG markup
                if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
                  return (
                    <div 
                      dangerouslySetInnerHTML={{ __html: sigData }}
                      style={{ maxHeight: 40, maxWidth: 200 }}
                    />
                  );
                }
                
                // Check if it's a base64 string (without data URL prefix)
                if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
                  return (
                    <SignatureImage 
                      src={`data:image/png;base64,${sigData}`} 
                      alt="Student Signature" 
                    />
                  );
                }
                
                // Check if it's a file path
                if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
                  return (
                    <SignatureImage src={sigData} alt="Student Signature" />
                  );
                }
                
                // Return name if no valid signature data
                return <SigValue>{student.firstName} {student.lastName}</SigValue>;
              })()}
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Date:</SigLabel>
              <DateValue>{dateStr}</DateValue>
            </SignatureRow>
          </SignatureSection>

        </FormContent>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default CellPhonePolicyForm;
